import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn("relative w-full overflow-auto md:overflow-visible", wrapperClassName)}>
    <table
      ref={ref}
      className={cn(
        "w-full caption-bottom text-sm rounded-lg overflow-hidden",
        // Modern clean design for light theme
        "bg-card dark:bg-black/20",
        "border border-border/60 dark:border-white/10",
        "shadow-sm dark:shadow-lg dark:shadow-black/10",
        className
      )}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead 
    ref={ref} 
      className={cn(
        "[&_tr]:border-b",
        // Modern clean header design
        "bg-muted/40 dark:bg-black/30",
        "border-b border-border dark:border-white/10",
        className
      )}
    {...props} 
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/20 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
      className={cn(
        "border-b border-border/60 dark:border-white/5",
        "transition-all duration-200",
        // Modern clean row hover
        "hover:bg-muted/30 dark:hover:bg-white/5",
        "data-[state=selected]:bg-muted/50 dark:data-[state=selected]:bg-white/10",
        "even:bg-muted/10 dark:even:bg-black/10",
        className
      )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
      className={cn(
        "h-10 md:h-12 px-2 md:px-4 text-left align-middle font-semibold",
        "text-foreground [&:has([role=checkbox])]:pr-0",
        // Modern clean header cell
        "bg-transparent dark:bg-black/20",
        className
      )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-2 md:p-4 align-middle [&:has([role=checkbox])]:pr-0 break-words", className)}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
